/**
 * Global state management for user profile functionality using signals
 *
 * This state store manages all profile-related UI state and provides methods for
 * updating the user profile through the CQRS flow. It follows the same pattern
 * as ChatState and SessionState, using signals for reactive state management.
 */

import type { User } from 'firebase/auth';

import type { UpdateUserProfileSpec } from '@elara/domain/flows';
import type { ProfilePreference } from '@elara/domain/models/profile-preferences';
import type { Address, CategorizedPreference, PreferenceCategory } from '@elara/domain/models/schemas/shared-schemas';
import type { AdditionalTraveller } from '@elara/domain/models/schemas/TravelerProfileSchema';
import type { UserProfileData } from '@elara/domain/models/UserProfileSpec';
import type { PreferenceProposalChoice } from '@elara/domain/models/schemas/widget-schemas';

import type { Theme } from '@elara/client-core/ui-contracts/theme';

import type { UserProfileContainer } from '#containers/UserProfileContainer.js';
import type { Asset } from '#lib/asset.js';
import type { UserProfile } from '#models/UserProfileModel.js';
import type { FileUploadState } from './FileUploadState.js';
import type { SessionState } from './SessionState.js';

import { effect } from '@preact/signals-react';

import { quickHash } from '@elara/core/utils';
import { isCategorizedPreference } from '@elara/domain/models/profile-preferences';

import {
  trackAccountDeletionRequested,
  trackAvatarUpdated,
  trackCompanionRemoved,
  trackCompanionSaved,
  trackPreferenceAdded,
  trackPreferenceProposalDecided,
  trackPreferenceRemoved,
  trackPreferenceUpdated,
} from '#lib/analytics-events.js';
import { detectLocalePreferences } from '#lib/locale-utils.js';
import { createState } from '#lib/signals-utils.js';

/**
 * Max avatar upload size. The Storage rules cap uploads at 10 MB
 * (`isReasonableSize()` in `storage.rules`); we check a little below that client-side to
 * surface a friendly message before the upload starts rather than after a rules rejection.
 */
const MAX_AVATAR_BYTES = 8 * 1024 * 1024;

/**
 * Fixed storage filename for the user's avatar, under `users/{uid}/profile/`. A single, stable
 * object per user (re-uploads overwrite it) — Firebase mints a fresh download token on each
 * overwrite, so the `photoURL` we persist changes every time and the image never caches stale.
 */
const AVATAR_STORAGE_FILENAME = 'avatar';

/**
 * The main reactive store for profile management
 *
 * @remark All the getters in FlatStore are transformed into computed signals, the values are signals.
 * The functions are not touched, they are the actions on the state.
 */
export function createUserProfileState(
  sessionState: SessionState,
  userProfile: UserProfileContainer,
  fileUploadState: FileUploadState,
) {
  const state = createState({
    /** Loading state for profile operations */
    get isLoading(): boolean {
      // If session is loading, profile is also loading
      if (sessionState.isLoading || !sessionState.isAuthenticated) {
        return true;
      }

      // Check if profile is still loading from Firestore
      const { UserProfileClientModel } = userProfile;
      if (!sessionState.userId) return true;

      const profileHandle = UserProfileClientModel.get({ id: sessionState.userId });
      return !profileHandle.isReady;
    },
    /** Saving state for profile operations */
    isSaving: false,
    /** Error state for profile operations */
    error: undefined as Error | undefined,
    /** Whether an avatar upload/removal is in flight (drives the upload spinner + disables controls). */
    isUploadingAvatar: false,
    /** Progress (0–100) of the in-flight avatar upload. */
    avatarUploadProgress: 0,
    /** Error from the last avatar upload/removal, surfaced by the platform UI (toast). */
    avatarError: undefined as Error | undefined,
    /** Temporary form state for editing */
    editingProfile: {} as UserProfileData,
    /** Track original address to detect changes */
    originalAddress: undefined as Address | null | undefined,
    /**
     * The preference lists as editing began (member, then each companion by position), serialized.
     * `save()` sends a list only if the form changed it: the settings screens edit preferences through
     * the per-entry flows, so an untouched editing copy is stale and resending it would bring back an
     * entry just removed.
     */
    originalPreferences: { member: '', travellers: [] as string[] },
    /**
     * The companion list as editing began, serialized. `save()` sends `travellers` only if the form
     * changed it: companions are saved one at a time by id (`saveTraveller`), so an untouched copy is
     * stale, and resending it would undo a companion added, edited or removed elsewhere meanwhile.
     */
    originalTravellers: '[]',
    /** New attribute form state */
    newAttributeKey: '',
    newAttributeValue: '',
    profileUpdatedForHash: undefined as string | undefined,
    get needsProfileUpdate(): boolean {
      return (
        !!sessionState.user &&
        !sessionState.isLoading &&
        this.profileUpdatedForHash !== userProfileCacheKey(sessionState.user)
      );
    },
    stopMonitors: undefined as (() => void) | undefined,
    startMonitors() {
      if (this.stopMonitors) {
        console.info(
          '[UserProfileState] Stopping previous profile update monitor (potential bug - did we call startMonitors twice?)',
        );
        this.stopMonitors();
      }
      /**
       * Effect to sync photoURL from the auth provider (e.g. Google) to the profile document.
       * Only syncs photoURL on an ongoing basis — displayName is set once during creation.
       *
       * A user-uploaded custom avatar (`hasCustomAvatar`) always wins: we must NOT overwrite it with
       * the provider photo, or a Google user's uploaded avatar would silently revert on the next login
       * (ELARA-518). Because that decision depends on the loaded profile, we wait for the profile doc
       * before syncing (the effect re-runs when it loads) and claim the sync synchronously so our own
       * write below doesn't re-enter the effect.
       */
      const unsub = effect(() => {
        if (!this.needsProfileUpdate) return;
        const providerPhotoURL = sessionState.user?.photoURL;
        if (!providerPhotoURL) {
          // No provider photo to sync; mark handled so we don't re-check on every render.
          this.profileUpdatedForHash = userProfileCacheKey(sessionState.user);
          return;
        }
        // Wait until the profile doc has loaded so we can honour a custom avatar.
        const profile = this.profile;
        if (!profile) return; // re-runs once the profile listener is ready
        // Claim the sync synchronously so the write below doesn't re-enter this effect.
        this.profileUpdatedForHash = userProfileCacheKey(sessionState.user);
        if (profile.hasCustomAvatar) return; // custom upload wins over the provider photo
        console.info(`[UserProfileState] Updating user profile photoURL for ${sessionState.userId}`);
        userProfile.UserProfileClientModel.update({ photoURL: providerPhotoURL }).catch((error: unknown) => {
          console.error('[UserProfileState] Failed to update profile photoURL', { error });
          // Let a later change retry the sync.
          this.profileUpdatedForHash = undefined;
        });
      });
      this.stopMonitors = () => {
        console.info('[UserProfileState] Stopping profile update monitor');
        unsub();
        this.stopMonitors = undefined;
      };
      return this.stopMonitors;
    },
    /**
     * The user's profile document from Firestore.
     */
    get profile(): UserProfile | undefined {
      const { UserProfileClientModel } = userProfile;
      // Guard: Check if authState is initialized before accessing it
      if (!sessionState.userId) return;
      const profile = UserProfileClientModel.get({ id: sessionState.userId });
      if (!profile.isReady) return;
      return profile.data;
    },
    /**
     * Delegates to SessionState.isPrivileged for consistent privilege checking.
     * Privileged users can access debug features and view other users' trips.
     */
    get isPrivileged(): boolean {
      return sessionState.isPrivileged;
    },
    get canAccessAdmin(): boolean {
      return sessionState.canAccessAdmin;
    },
    /** Initialize editing state with current profile data */
    initializeEditing(): void {
      const profile = this.profile;
      if (profile) {
        // Create a copy of the profile, explicitly including address (even if undefined)
        const address = profile.address as Address | undefined;

        // Detect locale preferences if not already set
        const localePrefs = detectLocalePreferences();
        const attributes = {
          ...profile.attributes,
          // Only set defaults if not already present
          timezone: profile.attributes?.timezone || localePrefs.timezone,
          preferredCurrency: profile.attributes?.preferredCurrency || localePrefs.currency,
          locale: profile.attributes?.locale || localePrefs.locale,
        };

        this.editingProfile = { ...profile, address, attributes };
        this.originalAddress = address;
        this.originalPreferences = {
          member: JSON.stringify(profile.travelerProfile?.preferences ?? []),
          travellers: (profile.travellers ?? []).map((t) => JSON.stringify(t.preferences ?? [])),
        };
        this.originalTravellers = JSON.stringify(profile.travellers ?? []);
      } else {
        // For new profiles, use detected locale preferences
        const localePrefs = detectLocalePreferences();
        this.editingProfile = {
          attributes: {
            timezone: localePrefs.timezone,
            preferredCurrency: localePrefs.currency,
            locale: localePrefs.locale,
          },
        } as Partial<UserProfileData> as UserProfileData;
        this.originalAddress = undefined;
        this.originalTravellers = '[]';
      }
    },
    /** Reset editing state and clear errors */
    resetEditingState(): void {
      this.error = undefined;
    },
    /** Update a field in the editing profile */
    updateField<K extends keyof UserProfile>(field: K, value: UserProfile[K]): void {
      this.editingProfile = { ...this.editingProfile, [field]: value };
    },
    /** Add or update an attribute */
    setAttribute(key: string, value: string): void {
      this.editingProfile = { ...this.editingProfile, attributes: { ...this.editingProfile.attributes, [key]: value } };
    },
    /** Update the address field */
    updateAddress(address: Address | null | undefined): void {
      this.editingProfile = { ...this.editingProfile, address: address ?? undefined };
    },
    /** Remove an attribute */
    removeAttribute(key: string): void {
      const { [key]: _, ...rest } = this.editingProfile.attributes ?? {};
      this.editingProfile = { ...this.editingProfile, attributes: rest };
    },
    /** Update new attribute key */
    setNewAttributeKey(key: string): void {
      this.newAttributeKey = key;
    },
    /** Update new attribute value */
    setNewAttributeValue(value: string): void {
      this.newAttributeValue = value;
    },
    /** Add attribute from form and clear inputs */
    addAttributeFromForm(): void {
      if (this.newAttributeKey && this.newAttributeValue) {
        this.setAttribute(this.newAttributeKey, this.newAttributeValue);
        this.newAttributeKey = '';
        this.newAttributeValue = '';
      }
    },
    /** Set theme preference and save to profile */
    async setTheme(theme: Theme): Promise<void> {
      const { UserProfileClientModel } = userProfile;
      // If user is not authenticated, theme change is only applied locally (no persistence)
      if (!sessionState.isAuthenticated || !sessionState.userId) {
        return;
      }

      try {
        // Get current attributes or create empty object
        const currentAttributes = this.profile?.attributes || {};
        // Update theme in attributes
        const updatedAttributes = { ...currentAttributes, theme };
        // Save to profile
        await UserProfileClientModel.update({ attributes: updatedAttributes });
      } catch (err) {
        console.error('[UserProfileState] Failed to update theme preference', { error: err });
        // Don't throw - allow theme to be applied locally even if save fails
      }
    },
    /** Set sidebar open preference and save to profile */
    async setSidebarOpen(open: boolean): Promise<void> {
      const { UserProfileClientModel } = userProfile;
      if (!sessionState.isAuthenticated || !sessionState.userId) return;

      try {
        const currentAttributes = this.profile?.attributes || {};
        const updatedAttributes = { ...currentAttributes, sidebarOpen: open ? 'true' : 'false' };
        await UserProfileClientModel.update({ attributes: updatedAttributes });
      } catch (err) {
        console.error('[UserProfileState] Failed to update sidebar preference', { error: err });
      }
    },
    /**
     * Upload a user-selected image as the profile avatar (ELARA-518). Stores it under
     * `users/{uid}/profile/avatar` in Firebase Storage, then persists the resulting download URL as
     * `photoURL` and flags `hasCustomAvatar` so the provider-photo sync monitor won't overwrite it.
     * Validates type/size client-side for a friendly message; on failure sets `avatarError` and does
     * not throw, so the platform UI can surface it. The avatar visibly updating (reactive `photoURL`)
     * is the success signal.
     */
    async uploadAvatar(asset: Asset): Promise<void> {
      const userId = sessionState.userId;
      if (!sessionState.isAuthenticated || !userId) {
        this.avatarError = new Error('You need to be signed in to change your photo.');
        return;
      }
      if (!asset.mimeType.startsWith('image/')) {
        this.avatarError = new Error('Please choose an image file.');
        return;
      }
      if (asset.size > MAX_AVATAR_BYTES) {
        this.avatarError = new Error('Please choose an image smaller than 8 MB.');
        return;
      }
      this.isUploadingAvatar = true;
      this.avatarUploadProgress = 0;
      this.avatarError = undefined;
      try {
        const path = `users/${userId}/profile/${AVATAR_STORAGE_FILENAME}`;
        const photoURL = await fileUploadState.uploadFile(asset, path, (progress) => {
          this.avatarUploadProgress = progress;
        });
        await userProfile.UserProfileClientModel.update({ photoURL, hasCustomAvatar: true });
        trackAvatarUpdated();
        console.info('[UserProfileState] Avatar updated');
      } catch (err) {
        console.error('[UserProfileState] Failed to upload avatar', { error: err });
        this.avatarError = err instanceof Error ? err : new Error('Something went wrong updating your photo.');
      } finally {
        this.isUploadingAvatar = false;
      }
    },
    /**
     * Remove the avatar: clear `photoURL` so it falls back to initials. `hasCustomAvatar` is set so
     * the provider-photo sync monitor treats this as an explicit user choice and won't re-add the
     * auth provider (Google) photo on the next login — otherwise a "removed" provider photo would
     * silently reappear. Sets `avatarError` on failure instead of throwing.
     */
    async removeAvatar(): Promise<void> {
      if (!sessionState.isAuthenticated || !sessionState.userId) return;
      this.isUploadingAvatar = true;
      this.avatarError = undefined;
      try {
        await userProfile.UserProfileClientModel.update({ photoURL: null, hasCustomAvatar: true });
        console.info('[UserProfileState] Avatar removed');
      } catch (err) {
        console.error('[UserProfileState] Failed to remove avatar', { error: err });
        this.avatarError = err instanceof Error ? err : new Error('Something went wrong removing your photo.');
      } finally {
        this.isUploadingAvatar = false;
      }
    },
    /** Referral dialog visibility */
    isReferralDialogOpen: false,
    /** Referral code for the current user */
    referralCode: undefined as string | undefined,
    /** Referral URL for the current user */
    referralUrl: undefined as string | undefined,
    /** Whether the referral code is loading */
    isReferralLoading: false,
    /** Open the referral dialog and fetch the referral code */
    async openReferralDialog(): Promise<void> {
      this.isReferralDialogOpen = true;
      if (this.referralCode) return; // Already fetched
      this.isReferralLoading = true;
      try {
        const result = await userProfile.flows.getReferralCode({});
        this.referralCode = result.data.referralCode;
        this.referralUrl = result.data.referralUrl;
      } catch (err) {
        console.error('[UserProfileState] Failed to fetch referral code', { error: err });
      } finally {
        this.isReferralLoading = false;
      }
    },
    /** Close the referral dialog */
    closeReferralDialog(): void {
      this.isReferralDialogOpen = false;
    },
    /** Whether the delete-account sheet/dialog is open */
    isDeleteAccountSheetOpen: false,
    /** Submission lifecycle for the account-deletion request */
    deletionRequestState: 'idle' as 'idle' | 'submitting' | 'submitted' | 'error',
    /**
     * True when the member has a pending account-deletion request. Backed by the server-authoritative
     * `deletionRequestedAt` marker on the profile; drives the in-app pending banner and the
     * already-requested UI state.
     */
    get hasRequestedDeletion(): boolean {
      return Boolean(this.profile?.deletionRequestedAt);
    },
    /** Open the delete-account sheet/dialog. */
    openDeleteAccountSheet(): void {
      // Reset a stale error so the sheet reopens clean; a completed/pending request is reflected via
      // deletionRequestState / hasRequestedDeletion.
      if (this.deletionRequestState === 'error') this.deletionRequestState = 'idle';
      this.isDeleteAccountSheetOpen = true;
    },
    /** Close the delete-account sheet/dialog. */
    closeDeleteAccountSheet(): void {
      this.isDeleteAccountSheetOpen = false;
    },
    /**
     * Submit an account-deletion request. Marks the profile server-side, emails support + the member,
     * and fires the analytics event on success. The pending marker then arrives via the Firestore
     * listener, flipping `hasRequestedDeletion` to true.
     */
    async requestAccountDeletion(reason?: string): Promise<void> {
      if (this.deletionRequestState === 'submitting') return;
      this.deletionRequestState = 'submitting';
      try {
        const trimmed = reason?.trim();
        await userProfile.flows.requestAccountDeletion(trimmed ? { reason: trimmed } : {});
        this.deletionRequestState = 'submitted';
        trackAccountDeletionRequested();
      } catch (err) {
        this.deletionRequestState = 'error';
        console.error('[UserProfileState] Failed to request account deletion', { error: err });
      }
    },
    async decidePreferenceProposal(input: {
      chatId: string;
      messageId: string;
      proposalId: string;
      category: PreferenceCategory;
      decision: PreferenceProposalChoice;
    }): Promise<PreferenceProposalChoice> {
      const { category, ...flowInput } = input;
      const result = await userProfile.flows.decidePreferenceProposal(flowInput);
      const decision = result.data.decision;
      trackPreferenceProposalDecided({ category, decision });
      return decision;
    },
    /**
     * Adds a preference for the member, or for the companion with `travellerId`. Saved immediately and
     * on its own, so it can't race a profile save.
     */
    async addPreference(input: { value: string; category: PreferenceCategory; travellerId?: string }): Promise<void> {
      await userProfile.flows.addProfilePreference(input);
      trackPreferenceAdded({ category: input.category, for_companion: input.travellerId !== undefined });
    },
    /**
     * Saves one companion's details and returns their id. A new companion (no id) is created with the
     * preferences typed for them; an existing one keeps its stored preferences, which change through the
     * preference actions above.
     */
    async saveTraveller(traveller: AdditionalTraveller): Promise<string> {
      const { preferences, ...details } = traveller;
      const isNew = traveller.id === undefined;
      const result = await userProfile.flows.saveTraveller({
        traveller: isNew ? { ...details, preferences } : details,
      });
      trackCompanionSaved({ is_new: isNew });
      return result.data.id;
    },
    /** Removes one companion, with their preferences. */
    async removeTraveller(id: string): Promise<void> {
      await userProfile.flows.removeTraveller({ id });
      trackCompanionRemoved();
    },
    /** Rewords a saved preference ("avoid" entries are remove-only; the server refuses them). */
    async updatePreference(id: string, value: string): Promise<void> {
      // Looked up before the write, which changes the profile this reads.
      const found = findPreference(this.profile, id);
      await userProfile.flows.updateProfilePreference({ id, value });
      if (found) trackPreferenceUpdated({ category: found.entry.category, for_companion: found.forCompanion });
    },
    /** Removes a saved preference from whichever person holds it. */
    async removePreference(entry: CategorizedPreference): Promise<void> {
      await userProfile.flows.removeProfilePreference({ id: entry.id });
      trackPreferenceRemoved({ category: entry.category, source: entry.source === 'elara' ? 'elara' : 'user' });
    },
    /** Save the profile changes */
    async save(): Promise<void> {
      const { UserProfileClientModel } = userProfile;
      console.info('[UserProfileState] Saving profile updates');
      try {
        this.isSaving = true;
        this.error = undefined;
        // Filter out empty values
        const updates: UpdateUserProfileSpec['InputType'] = {};
        if (this.editingProfile.displayName !== undefined) {
          updates.displayName = this.editingProfile.displayName;
        }
        if (Object.keys(this.editingProfile.attributes ?? {}).length > 0) {
          updates.attributes = this.editingProfile.attributes;
        }
        // Include address if it was modified
        const editingAddress = this.editingProfile.address;
        const addressChanged = !addressesEqual(editingAddress, this.originalAddress);

        if (addressChanged) {
          // Send undefined to clear the address, or the address object to set it
          updates.address = editingAddress ?? undefined;
        }
        // Include traveler profile, travellers, and travel preferences if present
        // Preference lists and a companion list the form didn't change are left out, so the server
        // keeps what it has (see `originalPreferences` and `originalTravellers`).
        const { travelerProfile, travellers } = this.editingProfile;
        if (travelerProfile) {
          const unchanged = JSON.stringify(travelerProfile.preferences ?? []) === this.originalPreferences.member;
          updates.travelerProfile = unchanged ? omitPreferences(travelerProfile) : travelerProfile;
        }
        if (travellers && JSON.stringify(travellers) !== this.originalTravellers) {
          updates.travellers = travellers.map((traveller, index) =>
            JSON.stringify(traveller.preferences ?? []) === this.originalPreferences.travellers[index]
              ? omitPreferences(traveller)
              : traveller,
          );
        }
        if (this.editingProfile.phoneNumber !== undefined) {
          updates.phoneNumber = this.editingProfile.phoneNumber || undefined;
        }
        console.info('[UserProfileState] Sending update with data:', updates);
        await UserProfileClientModel.update(updates);
        console.info('[UserProfileState] Profile updated successfully');
        this.resetEditingState();
      } catch (err) {
        console.error('[UserProfileState] Failed to update profile', { error: err });
        this.error = toFriendlyProfileError(err);
      } finally {
        this.isSaving = false;
      }
    },
  });
  return state;
}

export type UserProfileState = ReturnType<typeof createUserProfileState>;

/** A saved preference by id, and whether it belongs to a companion rather than the member. */
function findPreference(
  profile:
    | {
        travelerProfile?: { preferences?: ProfilePreference[] };
        travellers?: Array<{ preferences?: ProfilePreference[] }>;
      }
    | undefined,
  id: string,
): { entry: CategorizedPreference; forCompanion: boolean } | undefined {
  const byId = (entry: ProfilePreference): entry is CategorizedPreference =>
    isCategorizedPreference(entry) && entry.id === id;
  const own = profile?.travelerProfile?.preferences?.find(byId);
  if (own) return { entry: own, forCompanion: false };
  for (const traveller of profile?.travellers ?? []) {
    const entry = traveller.preferences?.find(byId);
    if (entry) return { entry, forCompanion: true };
  }
  return;
}

/** A copy without `preferences`, so a save leaves the stored list as it is. */
function omitPreferences<T extends { preferences?: unknown }>(holder: T): Omit<T, 'preferences'> {
  const { preferences: _omitted, ...rest } = holder;
  return rest;
}

/**
 * Deep equality check for Address objects
 */
function addressesEqual(a: Address | undefined | null, b: Address | undefined | null): boolean {
  // Both are null/undefined - equal
  if (!a && !b) return true;
  // One is null/undefined and the other isn't - not equal
  if (!a || !b) return false;
  // Compare all fields, handling optional fields correctly
  return (
    (a.city ?? undefined) === (b.city ?? undefined) &&
    (a.country ?? undefined) === (b.country ?? undefined) &&
    (a.countryCode ?? undefined) === (b.countryCode ?? undefined) &&
    (a.line1 ?? undefined) === (b.line1 ?? undefined) &&
    (a.line2 ?? undefined) === (b.line2 ?? undefined) &&
    (a.postalCode ?? undefined) === (b.postalCode ?? undefined)
  );
}

/**
 * A flow input-validation failure surfaces as a raw Zod-validation-error string (e.g. "appFlows.input
 * Invalid router input: ..."), which reads as a stack trace rather than something a user should see.
 * Screens should validate required fields (a name, etc.) before calling `save()`
 * so this rarely fires; this is a defense-in-depth fallback for whatever slips through.
 */
function toFriendlyProfileError(err: unknown): Error {
  const error = err instanceof Error ? err : new Error('Failed to update profile');
  if (/^\S+\.input Invalid router input/.test(error.message)) {
    return new Error('Something went wrong saving your changes. Please check your entries and try again.');
  }
  return error;
}

function userProfileCacheKey(user: User | undefined) {
  if (!user) return 'no-user';
  return quickHash({
    uid: user.uid,
    email: user.email,
    displayName: user.displayName,
    photoURL: user.photoURL,
    attributes: user.providerData,
    metadata: user.metadata,
  });
}
